import {
  Text,
  Input,
  Stack,
  Radio,
  Button,
  Spinner,
  FormLabel,
  RadioGroup,
  FormControl,
  FormErrorMessage,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useMemo } from "react";

import {
  PASSPORT_RF,
  PASSPORT_FOREIGN,
  TRadioOption,
  EPassportFormState,
} from "../../../../store/slices/passportFormSliceCreator";
import { useLoginFormStore } from "../../../../store/loginFormStore";
import { useAuthorizationStore } from "../../../../store/authorizationStore";
import { ForeignPassportInfo } from "./ForeignPassportInfo";
import { usePassportFormState, usePassportFormStateReaction } from "./hooks";

const PASSPORTS_MAP = {
  PASSPORT_RF: {
    placeholder: "Серия и номер паспорта",
    maxLength: 11,
  },
  PASSPORT_FOREIGN: {
    placeholder: "Номер документа",
    maxLength: 15,
  },
} as const;

export function PassportForm() {
  const auth = useAuthorizationStore();
  const allowForeign = auth.version !== "keycloak" || auth.foreignPassport;

  useEffect(() => {
    if (!allowForeign) {
      useLoginFormStore.getState().setPassportType(PASSPORT_RF);
    }
  }, [allowForeign]);

  const passportForm = usePassportFormState();
  usePassportFormStateReaction();

  const currentConfig = useMemo(() => {
    return passportForm.passportType
      ? PASSPORTS_MAP[passportForm.passportType]
      : PASSPORTS_MAP.PASSPORT_RF;
  }, [passportForm.passportType]);

  const handleSubmit = (): void => {
    if (passportForm.formState === EPassportFormState.initial) {
      passportForm.setFormState(EPassportFormState.validating);
    }
  };

  const onRadioChange = (e: TRadioOption) => {
    passportForm.setPassportType(e);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    let value = input.value;

    if (passportForm.passportType === "PASSPORT_RF") {
      value = value.replace(/\D/g, "");

      if (value.length > 4) {
        value = value.slice(0, 4) + " " + value.slice(4, 10);
      }

      value = value.slice(0, 11);
    }

    if (passportForm.passportType === "PASSPORT_FOREIGN") {
      value = value.replace(/[^A-Za-z0-9]/g, "");
      value = value.toUpperCase();
      value = value.slice(0, 15);
    }

    input.value = value;
    passportForm.setPassportNumber(value);
    passportForm.setTextError("");
  };

  useEffect(() => {
    passportForm.setPassportNumber("");
    passportForm.setTextError("");
  }, [passportForm.passportType]);

  return (
    <Stack spacing="7px">
      <FormControl isInvalid={!!passportForm.errorMessageOption}>
        <FormLabel>Паспортные данные</FormLabel>
        {allowForeign && (
          <RadioGroup
            onChange={onRadioChange}
            value={passportForm.passportType ?? undefined}
          >
            <Stack direction="row" spacing="32px">
              <Radio value={PASSPORT_RF} colorScheme="brand" spacing="1">
                <Text variant="black" fontSize="14px">Паспорт РФ</Text>
              </Radio>

              <Radio value={PASSPORT_FOREIGN} colorScheme="brand" spacing="1">
                <Text variant="black" fontSize="14px">Иностранный Паспорт</Text>
              </Radio>
            </Stack>
          </RadioGroup>
        )}
        <FormErrorMessage>{passportForm.errorMessageOption}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!passportForm.errorMessageText}>
        <Input
          autoFocus
          key={passportForm.passportType ?? PASSPORT_RF}
          name="passport-number"
          value={passportForm.passportNumber}
          onChange={onInputChange}
          placeholder={currentConfig.placeholder}
          maxLength={currentConfig.maxLength}
          color="brand.500"
          _hover={{ borderColor: passportForm.errorMessageText ? "red.500" : "purple.500" }}
          _focus={{
            borderColor: passportForm.errorMessageText ? "red.500" : "purple.500",
            backgroundColor: "white",
          }}
        />

        <ForeignPassportInfo passportType={passportForm.passportType} />
        <FormErrorMessage>{passportForm.errorMessageText}</FormErrorMessage>
      </FormControl>

      <Button
        colorScheme="brand"
        mt="9px"
        onClick={handleSubmit}
        isLoading={passportForm.formState === EPassportFormState.submitting}
      >
        {passportForm.formState === EPassportFormState.initial
          ? "Продолжить"
          : <Spinner variant="white" />}
      </Button>
    </Stack>
  );
}
